import { createTheme } from '@mui/material';

export const muiTheme = createTheme({
    components: {
        MuiButton: {
            styleOverrides: {
                root: {
                    '&, & *': {
                        fontSize: 'calc(12rem/16)',
                    },
                    fontWeight: 500,
                    height: '2rem',
                    padding: '0 0.9375rem',
                    minWidth: '5rem',
                    fontFamily: ['Roboto', 'Arial', 'Helvetica'],
                },
            },
        },
        MuiDialog: {
            defaultProps: {
                scroll: 'body',
            },
            styleOverrides: {
                root: {
                    fontFamily: 'Roboto, Helvetica, Arial, sans-serif',
                    '& *:not(.react-date-picker, .react-date-picker *, .MuiFormControl-root *)': {
                        boxSizing: 'border-box',
                    },
                    '& .MuiDialog-container': {
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '100vw',
                        overflow: 'hidden',
                    },
                    '& .MuiDialog-paper': {
                        display: 'flex',
                        flexDirection: 'column',
                        maxHeight: 'calc(100vh - 1rem)',
                        width: '800px',
                        maxWidth: 'unset',
                        margin: '3rem auto',
                        padding: 'calc(30rem/16) calc(50rem/16) calc(20rem/16) calc(50rem/16)',
                        borderRadius: '15px',
                        boxShadow: '0 2px 9px 4px rgba(0, 0, 0, 0.2)',
                    },
                },
            },
        },
        MuiDialogTitle: {
            styleOverrides: {
                root: ({ theme }) => ({
                    padding: 0,
                    margin: '0 calc(50rem/16)',
                    textAlign: 'center',
                    color: theme.palette.secondary.contrastText,
                    fontSize: 'calc(30rem/16)',
                    fontFamily: 'inherit',
                    fontWeight: 600,
                    borderBottom: `1px solid rgba(151, 151, 151, 0.16)`,
                    '& *': {
                        padding: 0,
                        margin: 0,
                    },
                    '& strong': {
                        color: '#d24444',
                    },
                    '& span': {
                        color: theme.palette.primary.main,
                    },
                }),
            },
        },
        MuiDialogContent: {
            styleOverrides: {
                root: ({ theme }) => ({
                    display: 'flex',
                    flexDirection: 'column',
                    flexGrow: 1,
                    minHeight: 0,
                    overflow: 'visible',
                    padding: 0,
                    '& > .MuiBox-root:first-child': {
                        '&::-webkit-scrollbar': {
                            width: '0.5rem',
                        },
                        '&::-webkit-scrollbar-track-piece': {
                            border: 'solid 3px transparent',
                            boxShadow: 'inset 0 0 10px 10px transparent',
                        },
                        '&::-webkit-scrollbar-thumb': {
                            backgroundColor: theme.palette.primary.main,
                            borderRadius: '8px',

                            '&:hover': {
                                backgroundColor: '#0276d6',
                            },
                        },
                        display: 'flex',
                        flexDirection: 'column',
                        flexGrow: 1,
                        minHeight: 0,
                        overflow: 'auto',
                        margin: 'calc(10rem/16) 0 0 0',
                        color: theme.palette.grey['table-text'],
                        '& .MuiBox-root': {
                            margin: 0,
                        },
                        '& > .MuiTypography-root:first-child': {
                            margin: 'calc(20rem/16) 0',
                        },
                        '& > .MuiTypography-root.note:last-child': {
                            marginTop: 'calc(12rem/16)',
                            fontSize: 'calc(12rem/16)',
                            fontStyle: 'italic',
                        },
                    },
                    '& > .MuiBox-root:last-child': {
                        margin: 'calc(30rem/16) 0 0 0',
                        display: 'flex',
                        justifyContent: 'space-between',
                        width: '100%',
                        '& > .MuiButton-root': {
                            boxShadow: 3,
                            fontSize: 'calc(12rem/16)',
                            fontWeight: 600,
                            margin: 0,
                        },
                        // Button alignment. Handles cases when there are 1, 2, or 3 buttons
                        '& > .MuiButton-root:first-child, & .MuiButton-root:nth-child(2):not(& > .MuiButton-root:last-child)': {
                            marginLeft: 'auto',
                            backgroundColor: theme.palette.secondary.main,
                            color: theme.palette.secondary.contrastText,
                        },
                        '& > .MuiButton-root:last-child:not(& > .MuiButton-root:first-child)': {
                            marginLeft: 'calc(20rem/16)',
                        },
                        // When only one button
                        '& > .MuiButton-root:last-child:first-child': {
                            backgroundColor: theme.palette.primary.main,
                            color: theme.palette.secondary.main,
                        },
                        '& > .MuiButton-root:nth-last-child(3)': {
                            marginLeft: 0,
                            backgroundColor: theme.palette.error.main,
                            color: '#FFFFFF',
                        },
                    },
                }),
            },
        },
        MuiTablePagination: {
            styleOverrides: {
                root: ({ theme }) => {
                    return {
                        textAlign: 'center',
                        width: '100%',
                        '& div.MuiToolbar-root': {
                            display: 'inline-flex',
                            margin: '0 auto',
                            '& .MuiTablePagination-displayedRows': {
                                fontWeight: 600,
                            },
                            '& .blue-text': {
                                color: theme.palette.primary.main,
                            },
                        },
                    };
                },
            },
        },
        MuiSelect: {
            styleOverrides: {
                root: ({ theme }) => {
                    return {
                        '& .MuiSelect-icon': {
                            right: '14px',
                        },
                    };
                },
            },
        },
    },
    palette: {
        primary: {
            main: '#014E8D',
        },
        secondary: {
            main: '#E2E6E9',
            contrastText: '#3C3E45',
        },
        error: {
            main: '#D24444',
            backgroundColor: '#FDF1F0',
        },
        warning: {
            main: '#FA6400',
        },
        approved: {
            main: '#416AD6',
        },
        submitted: {
            main: '#0091FF',
        },
        green: {
            main: '#586874',
        },
        grey: {
            button: '#3C3E45',
            border: '#CACDD1',
            header: '#586874',
            main: '#979797',
            'table-text': '#6D7278',
            'field-text': '#231C32',
            'field-label': '',
            'disabled-background': '#F5F7FF',
        },
        greyButton: {
            main: '#E2E6E9',
        },
    },
    typography: {
        h1: {
            fontSize: '36px',
            fontWeight: 700,
        },
        h2: {
            fontSize: '18px',
            fontWeight: 600,
        },
        button: {
            textTransform: 'none',
        },
    },
    greyButton: {
        main: '#E2E6E9',
    },
});

